import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import QRCodeStyling from 'qr-code-styling';
import { useEffect } from 'react';
import { IdvProvider, toWireFlowType } from 'src/features/property';
import { configVars } from 'src/helpers';
import { useSnackbarProvider } from 'src/hooks';
import {
  addCircularBorderWithDots,
  calculateQrSizeFromCanvasSize,
  DESIRED_CANVAS_SIZE
} from '../utils/qrCodeCanvas';
import {
  applyPdfStyling,
  createHiddenIframe,
  PDF_RENDER_CONFIG,
  waitForContentReady
} from '../utils/qrCodePdf';
import { generateQrCodeHtml } from '../utils/qrCodeTemplate';

interface UseQrCodeGeneratorParams {
  propertyShortId: string;
  propertyName: string;
  companyName?: string;
  isCpnEnabled: boolean;
}

const LOG_PREFIX = 'identityVerification/useQrCodeGenerator';

export const useQrCodeGenerator = (params: UseQrCodeGeneratorParams) => {
  const { propertyShortId, propertyName, companyName, isCpnEnabled } = params;
  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      // Ensure the message data is an object before accessing its properties
      if (!event.data || typeof event.data !== 'object') {
        return;
      }
      if (event.data.type === 'GENERATE_QR_PDF' && event.source) {
        const sourceWindow = event.source as Window;
        try {
          const childDoc = sourceWindow.document;
          const htmlContent = childDoc.documentElement.outerHTML;

          const { iframe, cleanup } = createHiddenIframe();

          if (iframe.contentDocument) {
            iframe.contentDocument.open();
            iframe.contentDocument.write(htmlContent);
            iframe.contentDocument.close();

            await waitForContentReady(iframe.contentDocument);

            const body = iframe.contentDocument.body;
            if (!body) {
              throw new Error('Body not found');
            }

            applyPdfStyling(iframe.contentDocument);

            const canvas = await html2canvas(body, {
              backgroundColor: PDF_RENDER_CONFIG.BACKGROUND_COLOR,
              scale: PDF_RENDER_CONFIG.CANVAS_SCALE,
              logging: process.env.NODE_ENV === 'development',
              useCORS: true,
              allowTaint: true,
              windowWidth: PDF_RENDER_CONFIG.VIEWPORT_WIDTH,
              windowHeight: PDF_RENDER_CONFIG.VIEWPORT_HEIGHT,
              scrollY: 0,
              scrollX: 0
            });

            const imgData = canvas.toDataURL('image/png', 1.0);

            const imgWidth = canvas.width / 2;
            const imgHeight = canvas.height / 2;

            const pdf = new jsPDF({
              orientation: imgHeight > imgWidth ? 'portrait' : 'landscape',
              unit: 'px',
              format: [imgWidth, imgHeight]
            });

            pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, '', 'SLOW');
            pdf.save('identity-verification-qr-code.pdf');

            cleanup();
            sourceWindow.postMessage({ type: 'QR_PDF_COMPLETE' }, window.location.origin);
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          const errorStack = error instanceof Error ? error.stack : undefined;

          // Structured error logging for DataDog observability
          console.error(`${LOG_PREFIX}/generatePDF`, {
            error: errorMessage,
            stack: errorStack,
            propertyShortId,
            propertyName,
            companyName,
            operation: 'qr_code_pdf_generation',
            phase: 'pdf_rendering'
          });

          showSnackbar(VariantType.error, `Error generating PDF: ${errorMessage}`, SnackTypes.none);

          sourceWindow.postMessage(
            {
              type: 'QR_PDF_ERROR',
              error: errorMessage
            },
            window.location.origin
          );
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    showSnackbar,
    VariantType.error,
    SnackTypes.none,
    propertyShortId,
    propertyName,
    companyName
  ]);

  const renderQrCode = async (config: {
    url: string;
    logoUrl: string;
    provider: IdvProvider;
  }): Promise<void> => {
    const newTab = window.open('', '_blank');
    if (!newTab) {
      showSnackbar(
        VariantType.error,
        'Popup was blocked. Please allow popups for this site.',
        SnackTypes.none
      );
      return;
    }

    try {
      const qrSize = calculateQrSizeFromCanvasSize(DESIRED_CANVAS_SIZE);
      const qrCode = new QRCodeStyling({
        width: qrSize,
        height: qrSize,
        type: 'canvas',
        data: config.url,
        margin: 0.6,
        image: config.logoUrl,
        imageOptions: {
          crossOrigin: 'anonymous',
          imageSize: config.provider === 'incode' ? 0.5 : 0.4,
          margin: config.provider === 'incode' ? 50 : 40,
          hideBackgroundDots: true
        },
        dotsOptions: {
          color: '#1a1a4d',
          type: 'dots'
        },
        backgroundOptions: {
          color: '#ffffff'
        },
        cornersSquareOptions: {
          color: '#1a1a4d',
          type: 'extra-rounded'
        },
        cornersDotOptions: {
          color: '#1a1a4d',
          type: 'dot'
        },
        qrOptions: {
          errorCorrectionLevel: 'H'
        }
      });

      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      document.body.appendChild(tempContainer);

      await qrCode.append(tempContainer);

      // Wait for the QR code to be fully rendered including the center logo image.
      // getRawData() returns a promise that resolves only after all async rendering
      // (including image loading) is complete. This prevents the race condition where
      // the canvas is captured before the logo finishes loading.
      await qrCode.getRawData('png');

      const qrCanvas = tempContainer.querySelector('canvas');
      if (!qrCanvas) {
        throw new Error('Failed to generate QR code canvas');
      }

      const qrImageCanvas = document.createElement('canvas');
      qrImageCanvas.width = qrCanvas.width;
      qrImageCanvas.height = qrCanvas.height;
      const qrCtx = qrImageCanvas.getContext('2d');
      if (qrCtx) {
        qrCtx.fillStyle = '#ffffff';
        qrCtx.fillRect(0, 0, qrImageCanvas.width, qrImageCanvas.height);
        qrCtx.drawImage(qrCanvas, 0, 0);
      }

      const canvasWithBorder = addCircularBorderWithDots(qrImageCanvas);
      const base64data = canvasWithBorder.toDataURL('image/png', 1.0);

      document.body.removeChild(tempContainer);

      const html = generateQrCodeHtml({
        propertyName,
        companyName,
        qrCodeBase64: base64data,
        provider: config.provider
      });

      newTab.document.write(html);
      newTab.document.close();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorStack = error instanceof Error ? error.stack : undefined;

      console.error(`${LOG_PREFIX}/generateQrCode`, {
        error: errorMessage,
        stack: errorStack,
        propertyShortId,
        provider: config.provider,
        operation: 'qr_code_generation',
        phase: 'qr_canvas_generation'
      });

      newTab.close();
      showSnackbar(VariantType.error, 'Unable to generate QR code', SnackTypes.none);
    }
  };

  const generateQrCode = async (type: 'basic' | 'enhanced'): Promise<void> => {
    const flowType = toWireFlowType(type, isCpnEnabled);
    const url = `${configVars.epa_url}/id-verification/public/${propertyShortId}?flowType=${flowType}`;
    const logoUrl = `${process.env.PUBLIC_URL}/assets/images/clearme-logo.svg`;
    return renderQrCode({ url, logoUrl, provider: 'clear' });
  };

  const generateIncodeQrCode = async (): Promise<void> => {
    const url = `${configVars.epa_url}/id-verification/public/${propertyShortId}`;
    const logoUrl = `${process.env.PUBLIC_URL}/assets/images/incode.png`;
    return renderQrCode({ url, logoUrl, provider: 'incode' });
  };

  return {
    generateQrCode,
    generateIncodeQrCode
  };
};
